/**
 * Ship sprite loading + icon resolution.
 * Cargo, hangar, and shop use photoreal module photos.
 */
import { getNeonIcon, getNeonShipPreview } from './neonIcons.js';

const BASE = (typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL) || './';

function asset(path) {
    // Ensure single slash join: BASE may be './' or '/repo/'
    const b = BASE.endsWith('/') ? BASE : BASE + '/';
    const p = path.startsWith('/') ? path.slice(1) : path;
    return b + p;
}

const SHIP_SPRITE_URL = asset('assets/ships/player-sprite.png');
const HANGAR_URL = asset('assets/ships/hangar-preview.png');

const MODULE_URLS = {
    weapon:  asset('assets/modules/weapon.png'),
    weapon2: asset('assets/modules/weapon2.png'),
    shield:  asset('assets/modules/shield.png'),
    engine:  asset('assets/modules/engine.png'),
    reactor: asset('assets/modules/reactor.png'),
    hull:    asset('assets/modules/hull.png'),
    sensor:  asset('assets/modules/sensor.png'),
    cargo:   asset('assets/modules/cargo.png'),
    special: asset('assets/modules/special.png'),
    wing:    asset('assets/modules/wing.png'),
    crystal: asset('assets/modules/crystal.png'),
    fuel:    asset('assets/modules/fuel.png'),
    scrap:   asset('assets/modules/scrap.png'),
    chip:    asset('assets/modules/chip.png'),
    drone:   asset('assets/modules/drone.png'),
    armor:   asset('assets/modules/armor.png'),
};

let shipImg = null;
let hangarImg = null;
const moduleImgs = {};
let loadPromise = null;

export function loadShipAssets() {
    if (loadPromise) return loadPromise;
    loadPromise = Promise.all([
        loadImg(SHIP_SPRITE_URL).then(img => { shipImg = img; }),
        loadImg(HANGAR_URL).then(img => { hangarImg = img; }),
        ...Object.entries(MODULE_URLS).map(([slot, url]) =>
            loadImg(url).then(img => { moduleImgs[slot] = img; }).catch(() => {})
        ),
    ]).catch(err => console.warn('[shipSprites] load failed', err));
    return loadPromise;
}

function loadImg(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('fail ' + src));
        img.src = src;
    });
}

export function getShipSprite() {
    return shipImg;
}

export function getHangarPreviewUrl(color = '#00d9ff') {
    // Neon ship preview (not photo)
    return getNeonShipPreview(color, 220);
}

export function getModuleSpriteUrl(slotOrShape) {
    return MODULE_URLS[slotOrShape] || null;
}

/** Photo for the part. Neon drawing only if that shape has no photo. */
export function resolveItemIcon(item) {
    if (!item) return '';
    const photo = item.shape && MODULE_URLS[item.shape];
    if (photo) return photo;
    if (item.shape) {
        return getNeonIcon(
            item.shape,
            item.color || '#00d9ff',
            item.rarity || 'common',
            item.seed || 0,
            { broken: !!item.broken }
        );
    }
    if (typeof item.icon === 'string' && item.icon) return item.icon;
    return '';
}

/**
 * Draw player ship PNG with true alpha + pseudo-3D bank/pitch.
 * Sprite faces UP; game angle 0 = right → rotate by angle + PI/2.
 *
 * @param {number} bank  -1..1 lean (roll) into turns
 * @param {number} pitch -1..1 nose attitude
 */
export function drawShipSprite(ctx, x, y, angle, size = 28, flash = false, bank = 0, pitch = 0) {
    const img = shipImg;
    if (!img || !img.complete || !img.naturalWidth) return false;

    const aspect = img.naturalWidth / img.naturalHeight;
    let h = size * 2.6;
    let w = h * aspect;
    if (w < size * 2.2) {
        w = size * 2.2;
        h = w / aspect;
    }

    // Clamp visual orientation
    const b = Math.max(-1, Math.min(1, bank));
    const p = Math.max(-0.7, Math.min(0.7, pitch));

    // Fake roll: foreshorten width, slight height gain, shear for depth
    const roll = b * 0.9;
    const sx = Math.max(0.38, Math.cos(roll * 0.95));
    const sy = 1 + Math.abs(b) * 0.12 + p * 0.14;
    // Shear X based on bank (wing-up illusion)
    const shearX = b * 0.28;
    // Shear Y / skew from pitch (nose lift/drop)
    const shearY = p * 0.16;

    ctx.save();
    ctx.translate(x, y);
    // Yaw
    ctx.rotate(angle + Math.PI / 2);
    // Extra tiny roll wobble for life
    ctx.rotate(b * 0.08);
    // Affine pseudo-3D: [sx, shearY, shearX, sy, 0, 0] applied via transform
    // canvas transform(a,b,c,d,e,f): x' = a*x + c*y + e, y' = b*x + d*y + f
    ctx.transform(sx, shearY, shearX, sy, 0, p * size * 0.35);

    if (flash) ctx.filter = 'brightness(1.8) saturate(1.2)';

    // Soft under-glow (stretched with ship)
    ctx.globalAlpha = 0.28 + Math.abs(b) * 0.1;
    ctx.shadowColor = '#00d9ff';
    ctx.shadowBlur = 16 + Math.abs(b) * 8;
    ctx.drawImage(img, -w / 2, -h / 2, w, h);

    // Main sprite
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
    ctx.drawImage(img, -w / 2, -h / 2, w, h);

    // Specular-ish edge highlight when banked (fake light from “above”)
    if (Math.abs(b) > 0.08) {
        ctx.globalAlpha = Math.min(0.22, Math.abs(b) * 0.28);
        ctx.globalCompositeOperation = 'lighter';
        // Slight offset highlight toward high wing
        const hx = -b * 3;
        ctx.drawImage(img, -w / 2 + hx, -h / 2 - 1, w, h);
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1;
    }

    ctx.filter = 'none';
    ctx.restore();
    return true;
}
